import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import AppointmentFormModal from './AppointmentFormModal';
import { WarrantyStatus } from './WarrantyStatus';
import { ApexReference, JobSearch } from './ApexReference';
import { JobDetailLauncher } from './JobDetailModal';
import { matchesJobSearch } from '../lib/jobReference';

export default function AdminAppointments() {
  const [appointments, setAppointments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [search, setSearch] = useState('');
  const visibleAppointments = appointments.filter(job => matchesJobSearch(job, search));

  useEffect(() => {
    fetchAppointments();
    fetchUsers();
  }, []);

  const fetchAppointments = async () => {
    try {
      const response = await axios.get(`${API}/admin/appointments`);
      setAppointments(response.data);
    } catch (error) {
      toast.error('Failed to fetch appointments');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    }
  };

  const handleDelete = async (appointmentId) => {
    if (!window.confirm('DELETE THIS APPOINTMENT? [Y/N]')) return;

    try {
      await axios.delete(`${API}/appointments/${appointmentId}`);
      toast.success('Appointment deleted');
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete appointment');
    }
  };

  const handleResendEmail = async (appointmentId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/appointments/${appointmentId}/resend-email`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Email resent successfully!');
    } catch (error) {
      toast.error('Failed to resend email');
    }
  };

  const handleEdit = (appointment) => {
    setEditingAppointment(appointment);
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingAppointment(null);
    fetchAppointments();
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? user.username : 'Unknown';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Urgent': return '#FF0000';
      case 'High': return '#FF6B00';
      case 'Medium': return '#FFFF00';
      case 'Low': return '#00FF00';
      default: return '#FFFFFF';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open': return '#00FFFF';
      case 'Scheduled': return '#00FFFF';
      case 'In Progress': return '#FFFF00';
      case 'Waiting for Customer': return '#FF00FF';
      case 'Waiting for Parts': return '#FF6B00';
      case 'Completed': return '#00FF00';
      case 'Closed': return '#00FF00';
      case 'Cancelled': return '#FF0000';
      default: return '#FFFFFF';
    }
  };

  if (loading) {
    return (
      <div className="retro-panel text-center py-12">
        <div className="text-2xl glow-text" style={{ color: '#FF6B00' }}>
          [LOADING APPOINTMENTS...]
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="retro-panel mb-6">
        <button
          onClick={() => {
            setEditingAppointment(null);
            setShowForm(true);
          }}
          data-testid="add-appointment-button"
          className="retro-btn"
        >
          [+ CREATE APPOINTMENT]
        </button>
      </div>

      <div className="retro-panel job-admin-list">
        <h2 className="text-3xl font-bold mb-6 glow-text" style={{ color: '#FF6B00', fontFamily: 'Orbitron, sans-serif' }}>
          [JOB APPOINTMENTS] - TOTAL: {visibleAppointments.length}
        </h2>
        <JobSearch scope="admin" value={search} onChange={setSearch} />

        {visibleAppointments.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-2xl" style={{ color: '#ffffff' }}>
              [NO APPOINTMENTS]
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="retro-table" data-testid="appointments-table">
              <thead>
                <tr>
                  <th>CUSTOMER</th>
                  <th>ASSIGNED TO</th>
                  <th>DATE</th>
                  <th>TIME</th>
                  <th>JOB TYPE</th>
                  <th>PRIORITY</th>
                  <th>STATUS</th>
                  <th>LOCATION</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {visibleAppointments.map((appointment) => (
                  <tr key={appointment.id}>
                    <td data-label="Customer" style={{ color: '#ffffff', fontWeight: 700 }}><ApexReference job={appointment} scope="admin" /><div className="mt-2">{appointment.customer_name}</div></td>
                    <td data-label="Assigned to" style={{ color: '#00FFFF' }}>{getUserName(appointment.assigned_to_user_id)}</td>
                    <td data-label="Date">{appointment.appointment_date}</td>
                    <td data-label="Time">{appointment.start_time} - {appointment.end_time}</td>
                    <td data-label="Job type">{appointment.job_type}<WarrantyStatus appointment={appointment} /></td>
                    <td data-label="Priority" style={{ color: getPriorityColor(appointment.priority), fontWeight: 700 }}>
                      {appointment.priority}
                    </td>
                    <td data-label="Status" style={{ color: getStatusColor(appointment.status), fontWeight: 700 }}>
                      {appointment.status}
                    </td>
                    <td data-label="Location">{appointment.location}</td>
                    <td data-label="Actions">
                      <div className="flex flex-wrap gap-2">
                        <JobDetailLauncher appointment={appointment} onUpdate={fetchAppointments} scope="admin" />
                        <button
                          onClick={() => handleEdit(appointment)}
                          data-testid={`edit-appointment-${appointment.id}`}
                          className="px-3 py-1 text-sm border-2 font-semibold"
                          style={{
                            color: '#ffffff',
                            borderColor: '#ffffff',
                            background: 'transparent',
                            boxShadow: '0 0 5px rgba(255, 255, 255, 0.3)'
                          }}
                        >
                          EDIT
                        </button>
                        <button
                          onClick={() => handleResendEmail(appointment.id)}
                          data-testid={`resend-email-${appointment.id}`}
                          className="px-3 py-1 text-sm border-2 font-semibold"
                          style={{
                            color: '#00FF00',
                            borderColor: '#00FF00',
                            background: 'transparent',
                            boxShadow: '0 0 5px rgba(0, 255, 0, 0.3)'
                          }}
                          title="Resend appointment email to assigned user"
                        >
                          EMAIL
                        </button>
                        <button
                          onClick={() => handleDelete(appointment.id)}
                          data-testid={`delete-appointment-${appointment.id}`}
                          className="px-3 py-1 text-sm border-2 font-semibold"
                          style={{
                            color: '#FF6B00',
                            borderColor: '#FF6B00',
                            background: 'transparent',
                            boxShadow: '0 0 5px rgba(255, 107, 0, 0.3)'
                          }}
                        >
                          DEL
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <AppointmentFormModal
          appointment={editingAppointment}
          users={users}
          onSuccess={handleFormSuccess}
          onCancel={() => {
            setShowForm(false);
            setEditingAppointment(null);
          }}
        />
      )}
    </>
  );
}